// @flow
// ----------------------------------------------------------------------
// <copyright file='Izenda.ReportDetailFooterActions.js' company='Izenda'>
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import PropTypes from 'prop-types';
import { compose, withHandlers, setPropTypes } from 'recompose';
import EmailPopup from 'share/components/controls/CommonPopup/Izenda.EmailPopup';
import { MESSAGES } from 'logic/data/Izenda.ReportsConstants';
import routeUtils from 'share/utils/Izenda.RouteUtils';
import { EMAIL_TYPE, DELIVERY_METHOD } from 'logic/data/Izenda.EmailConstants';
import ReportUtils from 'logic/models/Izenda.ReportUtils';
import { SOURCE_PATH_QUERY_KEY } from 'logic/data/Izenda.RouteConstants';
import { isEmpty, get, curry } from 'lodash/fp';

/**
 * Delete Report
 * @param {object} props
 * @param {object} event
 */
const deleteReport = (props: any, event: any) => {
  const { boardDetail, reportSubCategory, loader, store, router } = props;
  const message = boardDetail.type === 0 ? MESSAGES.M05a : MESSAGES.M05b;

  props
    .confirmFn('Confirmation', {
      description: String.format(message, boardDetail.name)
    })
    .then(() => {
      loader.startLoading();
      return store.deleteReport(boardDetail, reportSubCategory);
    })
    .then(result => {
      !isEmpty(result) &&
        router.push(
          routeUtils.getReportUrl('List', {
            reportType: boardDetail.type
          })
        );
      return null;
    })
    .finally(loader.endLoading.bind(loader));
};

/**
 * Save Subscription
 * @param {object} props
 * @param {object} subData
 */
const onSaveSubscription = (props: any, subData: any) => {
  props.reportSubscriptionStore.model.updateSubscribedInstances(subData);
};

/**
 * on Click Design
 * @param {object} props
 */
const onClickDesign = (props: any) => {
  const boardDetail = props.boardDetail || {};
  props.history.push('/new/' + boardDetail.id);
};

/**
 * Open Email Popup
 * @param {object} props
 * @param {object} e
 */
const openEmailPopup = (props: any, e: any) => {
  props.checkValidReport().then(result => {
    if (!result) {
      return;
    }
    const { designerStore, store, boardDetail, localization } = props;
    const reportId = boardDetail.id;

    const handleCloseEmailPopup = email => {
      if (!email) {
        return false;
      }
      // No need to check filters when email report link
      if (get('deliveryMethod', email) === DELIVERY_METHOD.link) {
        return store.sendEmail(null, email, reportId);
      }

      return ReportUtils.checkRequiredFilters(designerStore, reportId).then(isValidFilter => {
        if (!isValidFilter) {
          return localization.getText('REPORTS_CONSTANTS_REPORT_MESSAGE_REQUIRED_FILTER_EMAIL');
        }
        return store.sendEmail(null, email, reportId);
      });
    };

    props.openPopup(EmailPopup, {
      onClosed: handleCloseEmailPopup,
      type: EMAIL_TYPE.report,
      reportId: reportId
    });
  });
};

/**
 * Click Open
 * @param {object} props
 * @param {object} _
 */
const handleClickOpen = (props: any, _: any) => {
  const { store, router } = props;
  const boardDetail = props.boardDetail || {};
  const reportId = boardDetail.id;
  const viewUrl = routeUtils.getReportUrl('View', { id: reportId });
  store.updateReportId(reportId);
  const previousLink = routeUtils.getPreviousLink();

  store.savePreviousLink && store.savePreviousLink(previousLink);
  router.push(viewUrl);
};

/**
 * Click QuickEdit
 * @param {object} props
 * @param {object} _
 */
const handleClickQuickEdit = (props: any, _: any) => {
  const { store, router } = props;
  props.checkValidReport().then(result => {
    const boardDetail = props.boardDetail || {};
    const previousLink = routeUtils.getPreviousLink();

    store.savePreviousLink && store.savePreviousLink(previousLink);
    const editUrl = routeUtils.getReportUrl('Edit', { id: boardDetail.id });
    result && router.push(editUrl);
  });
};

/**
 * Click Design
 * @param {object} props
 * @param {object} _
 */
const handleClickDesign = (props: any, _: any) => {
  const boardDetail = props.boardDetail || {};
  const reportId = boardDetail.id;
  const query = {};
  query[`${SOURCE_PATH_QUERY_KEY}`] = routeUtils.getPreviousLink();

  const viewReportDesignerUrl = {
    pathname: routeUtils.getReportDesignerUrl('ReportDesignerDetail', {
      reportId
    }),
    query
  };
  props.router.push(viewReportDesignerUrl);
};

/**
 * Click Subscribe
 * @param {object} props
 * @param {object} _
 */
const handleClickSubscribe = (props: any, _: any) => {
  const model = props.store.model;
  model.items.resetSubscribes();
  const boardDetail = props.boardDetail || {};
  props.checkValidReport().then(result => {
    result && boardDetail.toggleSubscribeMode();
  });
};

/**
 * Click Copy
 * @param {object} props
 * @param {object} _
 */
const handleClickCopy = (props: any, _: any) =>
  props.checkValidReport().then(result => {
    result && props.viewCopyPopup();
  });

/**
 * Click Move
 * @param {object} props
 * @param {object} _
 */
const handleClickMove = (props: any, _: any) =>
  props.checkValidReport().then(result => {
    result && props.viewMovePopup();
  });

/**
 * Click Export
 * @param {object} props
 * @param {any} callBack
 */
const handleClickExport = (props: any, callBack: any) =>
  props.checkValidReport().then(result => {
    result && callBack();
  });

export default compose(
  withHandlers({
    deleteReport: curry(deleteReport),
    onSaveSubscription: curry(onSaveSubscription),
    onClickDesign: curry(onClickDesign),
    openEmailPopup: curry(openEmailPopup),
    handleClickOpen: curry(handleClickOpen),
    handleClickQuickEdit: curry(handleClickQuickEdit),
    handleClickDesign: curry(handleClickDesign),
    handleClickSubscribe: curry(handleClickSubscribe),
    handleClickCopy: curry(handleClickCopy),
    handleClickMove: curry(handleClickMove),
    handleClickExport: curry(handleClickExport)
  }),
  setPropTypes({
    store: PropTypes.object.isRequired
  })
);



// WEBPACK FOOTER //
// ./ui/modules/Reports/ReportList/Izenda.ReportDetailFooterActions.js